import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs, updateDoc, doc, deleteDoc } from 'firebase/firestore';
import { db } from '../firebase';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar, Line, Doughnut } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

const RegisteredOwners = () => {
  const [owners, setOwners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [selectedOwner, setSelectedOwner] = useState(null);
  const [ownerStats, setOwnerStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(false);
  const [overallStats, setOverallStats] = useState({
    totalRevenue: 0,
    totalSessions: 0,
    revenueByOwner: [],
    revenueByMonth: [],
    sessionsByMonth: []
  });

  useEffect(() => {
    fetchOwners();
  }, [filter]);

  useEffect(() => {
    if (owners.length > 0) {
      fetchOverallStats();
    }
  }, [owners]);

  const fetchOwners = async () => {
    setLoading(true);
    try {
      let q;
      if (filter === 'all') {
        q = query(collection(db, 'parking-lot-owners'), where('role', '==', 'parking-lot-owner'));
      } else {
        q = query(collection(db, 'parking-lot-owners'),
          where('role', '==', 'parking-lot-owner'),
          where('status', '==', filter)
        );
      }

      const querySnapshot = await getDocs(q);
      const ownersData = [];
      querySnapshot.forEach((doc) => {
        ownersData.push({ id: doc.id, ...doc.data() });
      });

      ownersData.sort((a, b) => new Date(b.registeredAt) - new Date(a.registeredAt));
      setOwners(ownersData);
    } catch (error) {
      console.error('Error fetching owners:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchOverallStats = async () => {
    try {
      // Fetch all parking sessions
      const sessionsQuery = query(collection(db, 'parking-sessions'));
      const sessionsSnapshot = await getDocs(sessionsQuery);

      let totalRevenue = 0;
      let totalSessions = 0;
      const revenueByOwnerMap = {};
      const revenueByMonthMap = {};
      const sessionsByMonthMap = {};

      sessionsSnapshot.forEach((doc) => {
        const session = doc.data();
        totalSessions++;

        if (session.paid && session.amount) {
          const amount = session.amount / 100;
          totalRevenue += amount;

          // Revenue by owner
          const ownerId = session.ownerId || 'Unknown';
          if (!revenueByOwnerMap[ownerId]) {
            revenueByOwnerMap[ownerId] = { revenue: 0, sessions: 0 };
          }
          revenueByOwnerMap[ownerId].revenue += amount;
          revenueByOwnerMap[ownerId].sessions++;

          // Revenue by month
          if (session.createdAt) {
            const date = new Date(session.createdAt);
            const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
            if (!revenueByMonthMap[monthKey]) {
              revenueByMonthMap[monthKey] = 0;
            }
            revenueByMonthMap[monthKey] += amount;
          }
        }

        // Sessions by month
        if (session.createdAt) {
          const date = new Date(session.createdAt);
          const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
          if (!sessionsByMonthMap[monthKey]) {
            sessionsByMonthMap[monthKey] = 0;
          }
          sessionsByMonthMap[monthKey]++;
        }
      });

      // Map owner IDs to names
      const revenueByOwner = owners.map(owner => ({
        name: owner.name,
        revenue: revenueByOwnerMap[owner.ownerId]?.revenue || 0,
        sessions: revenueByOwnerMap[owner.ownerId]?.sessions || 0
      })).filter(o => o.revenue > 0 || o.sessions > 0).sort((a, b) => b.revenue - a.revenue);

      // Sort months and get last 6
      const sortedMonths = Object.keys(revenueByMonthMap).sort();
      const last6Months = sortedMonths.slice(-6);

      const revenueByMonth = last6Months.map(month => ({
        month: formatMonth(month),
        revenue: revenueByMonthMap[month] || 0
      }));

      const sessionsByMonth = last6Months.map(month => ({
        month: formatMonth(month),
        sessions: sessionsByMonthMap[month] || 0
      }));

      setOverallStats({
        totalRevenue,
        totalSessions,
        revenueByOwner,
        revenueByMonth,
        sessionsByMonth
      });
    } catch (error) {
      console.error('Error fetching overall stats:', error);
    }
  };

  const formatMonth = (monthKey) => {
    const [year, month] = monthKey.split('-');
    const date = new Date(year, parseInt(month) - 1);
    return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  };

  const fetchOwnerStats = async (owner) => {
    setLoadingStats(true);
    setSelectedOwner(owner);

    try {
      // Fetch parking sessions for this owner
      const sessionsQuery = query(
        collection(db, 'parking-sessions'),
        where('ownerId', '==', owner.ownerId)
      );
      const sessionsSnapshot = await getDocs(sessionsQuery);

      // Fetch parking lots for this owner
      const lotsQuery = query(
        collection(db, 'parking-lots'),
        where('ownerId', '==', owner.ownerId)
      );
      const lotsSnapshot = await getDocs(lotsQuery);

      let totalRevenue = 0;
      let todayRevenue = 0;
      let weekRevenue = 0;
      let monthRevenue = 0;
      let totalSessions = 0;
      let paidSessions = 0;
      let currentlyParked = 0;
      let totalDuration = 0;
      const revenueByDay = {};

      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const weekAgo = new Date(today);
      weekAgo.setDate(weekAgo.getDate() - 7);
      const monthAgo = new Date(today);
      monthAgo.setDate(monthAgo.getDate() - 30);

      sessionsSnapshot.forEach((doc) => {
        const session = doc.data();
        totalSessions++;

        if (session.paid && session.amount) {
          const amount = session.amount / 100;
          totalRevenue += amount;
          paidSessions++;

          const sessionDate = new Date(session.createdAt);
          if (sessionDate >= today) {
            todayRevenue += amount;
          }
          if (sessionDate >= weekAgo) {
            weekRevenue += amount;
          }
          if (sessionDate >= monthAgo) {
            monthRevenue += amount;

            // Track daily revenue for last 30 days
            const dayKey = sessionDate.toISOString().split('T')[0];
            if (!revenueByDay[dayKey]) {
              revenueByDay[dayKey] = 0;
            }
            revenueByDay[dayKey] += amount;
          }
        }

        if (!session.checkOutTime) {
          currentlyParked++;
        }

        if (session.duration) {
          totalDuration += session.duration;
        }
      });

      // Generate last 14 days for chart
      const last14Days = [];
      for (let i = 13; i >= 0; i--) {
        const date = new Date();
        date.setDate(date.getDate() - i);
        const dayKey = date.toISOString().split('T')[0];
        last14Days.push({
          date: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
          revenue: revenueByDay[dayKey] || 0
        });
      }

      const parkingLots = [];
      lotsSnapshot.forEach((doc) => {
        parkingLots.push({ id: doc.id, ...doc.data() });
      });

      setOwnerStats({
        totalRevenue,
        todayRevenue,
        weekRevenue,
        monthRevenue,
        totalSessions,
        paidSessions,
        currentlyParked,
        averageDuration: totalSessions > 0 ? Math.round(totalDuration / totalSessions) : 0,
        parkingLots,
        revenueByDay: last14Days
      });
    } catch (error) {
      console.error('Error fetching owner stats:', error);
    } finally {
      setLoadingStats(false);
    }
  };

  const updateOwnerStatus = async (ownerId, newStatus) => {
    try {
      const ownerRef = doc(db, 'parking-lot-owners', ownerId);
      await updateDoc(ownerRef, {
        status: newStatus,
        updatedAt: new Date().toISOString()
      });
      fetchOwners();
    } catch (error) {
      console.error('Error updating owner status:', error);
      alert('Failed to update status');
    }
  };

  const deleteOwner = async (ownerId) => {
    if (window.confirm('Are you sure you want to delete this parking lot owner?')) {
      try {
        await deleteDoc(doc(db, 'parking-lot-owners', ownerId));
        fetchOwners();
      } catch (error) {
        console.error('Error deleting owner:', error);
        alert('Failed to delete owner');
      }
    }
  };

  const getStatusBadge = (status) => {
    const statusStyles = {
      pending: 'bg-yellow-100 text-yellow-800',
      approved: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800',
      suspended: 'bg-gray-100 text-gray-800'
    };

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusStyles[status] || statusStyles.pending}`}>
        {status}
      </span>
    );
  };

  // Chart configurations
  const revenueByOwnerChartData = {
    labels: overallStats.revenueByOwner.slice(0, 10).map(o => o.name),
    datasets: [
      {
        label: 'Revenue ($)',
        data: overallStats.revenueByOwner.slice(0, 10).map(o => o.revenue),
        backgroundColor: 'rgba(99, 102, 241, 0.8)',
        borderColor: 'rgba(99, 102, 241, 1)',
        borderWidth: 1,
      },
    ],
  };

  const revenueByMonthChartData = {
    labels: overallStats.revenueByMonth.map(m => m.month),
    datasets: [
      {
        label: 'Revenue ($)',
        data: overallStats.revenueByMonth.map(m => m.revenue),
        borderColor: 'rgba(34, 197, 94, 1)',
        backgroundColor: 'rgba(34, 197, 94, 0.2)',
        tension: 0.3,
        fill: true,
      },
    ],
  };

  const sessionsByMonthChartData = {
    labels: overallStats.sessionsByMonth.map(m => m.month),
    datasets: [
      {
        label: 'Sessions',
        data: overallStats.sessionsByMonth.map(m => m.sessions),
        borderColor: 'rgba(59, 130, 246, 1)',
        backgroundColor: 'rgba(59, 130, 246, 0.2)',
        tension: 0.3,
        fill: true,
      },
    ],
  };

  const ownerRevenueChartData = ownerStats ? {
    labels: ownerStats.revenueByDay.map(d => d.date),
    datasets: [
      {
        label: 'Daily Revenue ($)',
        data: ownerStats.revenueByDay.map(d => d.revenue),
        borderColor: 'rgba(99, 102, 241, 1)',
        backgroundColor: 'rgba(99, 102, 241, 0.2)',
        tension: 0.3,
        fill: true,
      },
    ],
  } : null;

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
      },
    },
    scales: {
      y: {
        beginAtZero: true,
      },
    },
  };

  const barChartOptions = {
    ...chartOptions,
    indexAxis: 'y',
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Owners Table Section */}
      <div className="p-6 bg-white rounded-lg shadow">
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Registered Parking Lot Owners</h2>

          <div className="flex gap-2 mb-4">
            <button
              onClick={() => setFilter('all')}
              className={`px-4 py-2 rounded-md ${filter === 'all' ? 'bg-indigo-600 text-white' : 'bg-gray-200 text-gray-700'}`}
            >
              All ({owners.length})
            </button>
            <button
              onClick={() => setFilter('pending')}
              className={`px-4 py-2 rounded-md ${filter === 'pending' ? 'bg-indigo-600 text-white' : 'bg-gray-200 text-gray-700'}`}
            >
              Pending
            </button>
            <button
              onClick={() => setFilter('approved')}
              className={`px-4 py-2 rounded-md ${filter === 'approved' ? 'bg-indigo-600 text-white' : 'bg-gray-200 text-gray-700'}`}
            >
              Approved
            </button>
            <button
              onClick={() => setFilter('rejected')}
              className={`px-4 py-2 rounded-md ${filter === 'rejected' ? 'bg-indigo-600 text-white' : 'bg-gray-200 text-gray-700'}`}
            >
              Rejected
            </button>
          </div>
        </div>

        {owners.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500">No parking lot owners found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Email
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Address
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Stripe
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Registered
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {owners.map((owner) => (
                  <tr key={owner.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {owner.name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {owner.email}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      <div className="max-w-xs truncate" title={owner.parkingLotAddress}>
                        {owner.parkingLotAddress || 'N/A'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(owner.status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        owner.stripeAccountId
                          ? 'bg-green-100 text-green-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {owner.stripeAccountId ? 'Connected' : 'Not Connected'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(owner.registeredAt).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex gap-2">
                        <button
                          onClick={() => fetchOwnerStats(owner)}
                          className="text-indigo-600 hover:text-indigo-900 font-medium"
                        >
                          View Details
                        </button>
                        {owner.status === 'pending' && (
                          <>
                            <button
                              onClick={() => updateOwnerStatus(owner.id, 'approved')}
                              className="text-green-600 hover:text-green-900"
                            >
                              Approve
                            </button>
                            <button
                              onClick={() => updateOwnerStatus(owner.id, 'rejected')}
                              className="text-red-600 hover:text-red-900"
                            >
                              Reject
                            </button>
                          </>
                        )}
                        {owner.status === 'approved' && (
                          <button
                            onClick={() => updateOwnerStatus(owner.id, 'suspended')}
                            className="text-yellow-600 hover:text-yellow-900"
                          >
                            Suspend
                          </button>
                        )}
                        {owner.status === 'suspended' && (
                          <button
                            onClick={() => updateOwnerStatus(owner.id, 'approved')}
                            className="text-green-600 hover:text-green-900"
                          >
                            Reactivate
                          </button>
                        )}
                        <button
                          onClick={() => deleteOwner(owner.id)}
                          className="text-red-600 hover:text-red-900"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Overall Stats Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-green-100 text-green-600">
              <svg className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Total Revenue (All Owners)</p>
              <p className="text-2xl font-bold text-gray-900">${overallStats.totalRevenue.toFixed(2)}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-blue-100 text-blue-600">
              <svg className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Total Sessions</p>
              <p className="text-2xl font-bold text-gray-900">{overallStats.totalSessions}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-purple-100 text-purple-600">
              <svg className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Active Owners</p>
              <p className="text-2xl font-bold text-gray-900">{owners.filter(o => o.status === 'approved').length}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Revenue by Owner */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Revenue by Owner (Top 10)</h3>
          <div className="h-80">
            {overallStats.revenueByOwner.length > 0 ? (
              <Bar data={revenueByOwnerChartData} options={barChartOptions} />
            ) : (
              <div className="flex items-center justify-center h-full text-gray-500">
                No revenue data available
              </div>
            )}
          </div>
        </div>

        {/* Monthly Revenue Trend */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Monthly Revenue Trend</h3>
          <div className="h-80">
            {overallStats.revenueByMonth.length > 0 ? (
              <Line data={revenueByMonthChartData} options={chartOptions} />
            ) : (
              <div className="flex items-center justify-center h-full text-gray-500">
                No revenue data available
              </div>
            )}
          </div>
        </div>

        {/* Monthly Sessions Trend */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Monthly Sessions Trend</h3>
          <div className="h-80">
            {overallStats.sessionsByMonth.length > 0 ? (
              <Line data={sessionsByMonthChartData} options={chartOptions} />
            ) : (
              <div className="flex items-center justify-center h-full text-gray-500">
                No session data available
              </div>
            )}
          </div>
        </div>

        {/* Owner Revenue Distribution */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Revenue Distribution by Owner</h3>
          <div className="h-80">
            {overallStats.revenueByOwner.length > 0 ? (
              <Doughnut
                data={{
                  labels: overallStats.revenueByOwner.slice(0, 5).map(o => o.name),
                  datasets: [{
                    data: overallStats.revenueByOwner.slice(0, 5).map(o => o.revenue),
                    backgroundColor: [
                      'rgba(99, 102, 241, 0.8)',
                      'rgba(34, 197, 94, 0.8)',
                      'rgba(59, 130, 246, 0.8)',
                      'rgba(249, 115, 22, 0.8)',
                      'rgba(168, 85, 247, 0.8)',
                    ],
                    borderWidth: 1,
                  }],
                }}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: {
                      position: 'right',
                    },
                  },
                }}
              />
            ) : (
              <div className="flex items-center justify-center h-full text-gray-500">
                No revenue data available
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Owner Details Modal */}
      {selectedOwner && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-start mb-6">
                <div>
                  <h3 className="text-2xl font-bold text-gray-900">{selectedOwner.name}</h3>
                  <p className="text-gray-500">{selectedOwner.email}</p>
                  <p className="text-sm text-gray-400">{selectedOwner.parkingLotAddress}</p>
                </div>
                <button
                  onClick={() => {
                    setSelectedOwner(null);
                    setOwnerStats(null);
                  }}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {loadingStats ? (
                <div className="flex justify-center items-center h-64">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
                </div>
              ) : ownerStats ? (
                <div className="space-y-6">
                  {/* Stats Grid */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="bg-green-50 rounded-lg p-4">
                      <p className="text-sm text-green-600 font-medium">Total Revenue</p>
                      <p className="text-2xl font-bold text-green-700">${ownerStats.totalRevenue.toFixed(2)}</p>
                    </div>
                    <div className="bg-blue-50 rounded-lg p-4">
                      <p className="text-sm text-blue-600 font-medium">Today's Revenue</p>
                      <p className="text-2xl font-bold text-blue-700">${ownerStats.todayRevenue.toFixed(2)}</p>
                    </div>
                    <div className="bg-purple-50 rounded-lg p-4">
                      <p className="text-sm text-purple-600 font-medium">This Week</p>
                      <p className="text-2xl font-bold text-purple-700">${ownerStats.weekRevenue.toFixed(2)}</p>
                    </div>
                    <div className="bg-orange-50 rounded-lg p-4">
                      <p className="text-sm text-orange-600 font-medium">This Month</p>
                      <p className="text-2xl font-bold text-orange-700">${ownerStats.monthRevenue.toFixed(2)}</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="bg-gray-50 rounded-lg p-4">
                      <p className="text-sm text-gray-600 font-medium">Total Sessions</p>
                      <p className="text-2xl font-bold text-gray-700">{ownerStats.totalSessions}</p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <p className="text-sm text-gray-600 font-medium">Paid Sessions</p>
                      <p className="text-2xl font-bold text-gray-700">{ownerStats.paidSessions}</p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <p className="text-sm text-gray-600 font-medium">Currently Parked</p>
                      <p className="text-2xl font-bold text-gray-700">{ownerStats.currentlyParked}</p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <p className="text-sm text-gray-600 font-medium">Avg Duration</p>
                      <p className="text-2xl font-bold text-gray-700">{ownerStats.averageDuration} min</p>
                    </div>
                  </div>

                  {/* Revenue Chart */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="text-lg font-semibold text-gray-900 mb-4">Revenue (Last 14 Days)</h4>
                    <div className="h-64">
                      {ownerRevenueChartData && (
                        <Line data={ownerRevenueChartData} options={chartOptions} />
                      )}
                    </div>
                  </div>

                  {/* Parking Lots */}
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-3">Parking Lots ({ownerStats.parkingLots.length})</h4>
                    {ownerStats.parkingLots.length > 0 ? (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {ownerStats.parkingLots.map((lot) => (
                          <div key={lot.id} className="bg-gray-50 rounded-lg p-4">
                            <div className="flex justify-between items-start">
                              <div>
                                <p className="font-medium text-gray-900">{lot.name}</p>
                                <p className="text-sm text-gray-500">{lot.address}</p>
                              </div>
                              <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                                lot.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                              }`}>
                                {lot.status}
                              </span>
                            </div>
                            <p className="text-sm text-gray-500 mt-2">{lot.spots || 0} spots</p>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-gray-500">No parking lots created yet</p>
                    )}
                  </div>
                </div>
              ) : null}

              <div className="mt-6 flex justify-end">
                <button
                  onClick={() => {
                    setSelectedOwner(null);
                    setOwnerStats(null);
                  }}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RegisteredOwners;
